import type { LucideProps } from 'lucide-react';
import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router';
import { apiMode } from '@/api/mode';

type Icon = ComponentType<LucideProps>;

/**
 * Frontend module registry. The product is a set of modules (base, athletes, physio, later trainers, nutrition, ...), the way
 * Odoo's apps are. Each module owns its screens and contributes them to the shell: routes, menu entries, settings pages, reports,
 * Athlete 360 tabs, quick-create actions, a home page, and pieces of other modules' screens (slots). The shell composes all of that
 * from the modules the organization has *installed*, intersected with what the signed-in person may do.
 * Adding a module means adding one PlatformModule to `modules/registry.ts`: no shell changes.
 *
 * Direction of knowledge, checked by `architecture.test.ts`: base knows no module, athletes knows only base, physio knows athletes
 * and base. A module reaches into a module below it by importing it; it reaches into one above it only through a slot.
 */
export interface NavItem {
  id: string;
  label: string;
  to: string;
  icon: Icon;
  /** User needs at least one of these permissions to see the item. */
  permission?: string | string[];
  end?: boolean;
  children?: NavItem[];
  /** Put an item contributed by another module under this menu group. */
  parentId?: string;
  /** Section in the sidebar. */
  section: 'main' | 'module' | 'bottom';
  /** Sort order within the section. */
  order: number;
}

export interface AthleteTab {
  id: string;
  label: string;
  /** Path suffix under /athletes/:id ('' = overview). */
  path: string;
  permission?: string | string[];
  order: number;
  icon?: Icon;
}

/** A tab on a sport's page (`/sports/:sportId`): Base offers Details and related lists, and an app may add its own (Athletes adds Teams). */
export interface SportTab {
  id: string;
  label: string;
  /** Path suffix under /sports/:sportId ('' = details). */
  path: string;
  permission?: string | string[];
  /** A capability the sport must have for the tab to be there (a sport with no disciplines has no Disciplines tab). Left out, every sport has it. */
  requires?: string;
  order: number;
  icon?: Icon;
}

export interface QuickAction {
  id: string;
  label: string;
  icon: Icon;
  to: string;
  permission: string;
  order: number;
}

/** A page under /settings, listed in the settings menu and reachable at /settings/<path>. */
export interface SettingsSection {
  id: string;
  label: string;
  path: string;
  icon: Icon;
  permission: string | string[];
  order: number;
  component: ComponentType;
  /**
   * The page saves to the real service. While the desktop is connected to it (`VITE_API_MODE=live`), a page without this is left out
   * rather than shown working on sample data that would not be kept. Every page appears in the prototype.
   */
  live?: boolean;
}

/** A report: a card in the reports list and a page at /reports/<slug>. */
export interface ReportEntry {
  id: string;
  slug: string;
  title: string;
  description: string;
  icon: Icon;
  permission: string | string[];
  /** Contains health information (shown as "Clinical"); otherwise it is safe for coaches ("Coach-safe"). */
  clinical: boolean;
  order: number;
  component: ComponentType;
  /** Keep the route, but omit this report from the global Reports catalogue when it belongs in a module-specific menu. */
  showInReports?: boolean;
}

/** One step of what a person may do in an app, as it is chosen on the Users screen ("Viewer", "User", "Administrator"). */
export interface AccessLevel {
  id: string;
  label: string;
  description: string;
  /** What this level allows on top of the levels before it: choosing a level gives the person its own permissions and every earlier level's. */
  perms: string[];
}

/** One permission (or a few that belong together) chosen on its own, beside the levels: "Backup and restore". */
export interface AccessExtra {
  id: string;
  label: string;
  description: string;
  perms: string[];
}

/**
 * How an app's permissions are grouped to choose them by, the way an Odoo user form gives each app a dropdown (No access / User / Administrator).
 * Every permission the app declares belongs to exactly one level or extra. The service knows only permissions; this grouping is the interface's.
 */
export interface AppAccess {
  /** What the app is called where access is chosen. */
  title: string;
  /** Least to most: each includes the ones before it. */
  levels: AccessLevel[];
  extras?: AccessExtra[];
  /** One sentence per permission the app declares, for choosing them one at a time. */
  permissions: Record<string, string>;
  order: number;
}

/** Where "/" goes for someone: the first matching home (lowest order) wins, and base shows its own welcome when none does. */
export interface HomePage {
  order: number;
  permission?: string | string[];
  redirectTo?: string;
  component?: ComponentType;
}

/**
 * The places a module may put something into a screen that belongs to a module below it. The owner of the screen decides where a slot
 * is and what it is given, and declares it by merging into this interface (Athletes does, in `modules/athletes/desktop/slots.ts`);
 * whoever is installed decides what appears. With the contributing module uninstalled, the slot is empty.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface SlotProps {}
export type SlotName = keyof SlotProps;

export interface SlotContribution<N extends SlotName = SlotName> {
  id: string;
  order: number;
  /** For a slot whose screen lays its contributions out side by side: the heading of a column, the name of a view. */
  label?: string;
  /** The contribution appears only for someone holding at least one of these. Omit it to always mount the component (it decides itself). */
  permission?: string | string[];
  component: ComponentType<SlotProps[N]>;
}
export type SlotContributions = { [N in SlotName]?: SlotContribution<N>[] };

/** One sort of thing the global search can find: how its group is headed and drawn. A hit names its kind, and the app that owns the kind says how it looks. */
export interface SearchKind {
  kind: string;
  heading: string;
  icon: Icon;
}

export interface PlatformModule {
  /** The app id, the same as the backend's app label ('base', 'athletes', 'physio'). */
  id: string;
  nav: NavItem[];
  /** Pages inside the app shell (paths relative to the root). They exist only while the module is installed. */
  routes?: RouteObject[];
  /**
   * Pages a module adds under a route of a module below it, keyed by that route's `id` (Physiotherapy adds its tabs under the athlete's
   * page, `athlete`). They are mounted there when the router is built and exist only while this module is installed.
   */
  routeExtensions?: Record<string, RouteObject[]>;
  settingsSections?: SettingsSection[];
  reports?: ReportEntry[];
  /** How this app's permissions are grouped into levels for choosing a person's access. */
  access?: AppAccess;
  home?: HomePage;
  athleteTabs?: AthleteTab[];
  sportTabs?: SportTab[];
  quickActions?: QuickAction[];
  slots?: SlotContributions;
  /** What this app's results look like in the global search. */
  searchKinds?: SearchKind[];
}

export function hasAny(have: ReadonlySet<string>, needed?: string | string[]): boolean {
  if (!needed) return true;
  const list = Array.isArray(needed) ? needed : [needed];
  return list.some((p) => have.has(p));
}

const enabledOnly = (modules: PlatformModule[], enabled: string[]) => modules.filter((m) => enabled.includes(m.id));

export function composeNav(modules: PlatformModule[], enabled: string[], permissions: ReadonlySet<string>): NavItem[] {
  const visibleTree = (item: NavItem): NavItem => ({
    ...item,
    children: item.children?.filter((child) => hasAny(permissions, child.permission)).map(visibleTree),
  });
  const items = enabledOnly(modules, enabled)
    .flatMap((m) => m.nav)
    .filter((n) => hasAny(permissions, n.permission))
    .map(visibleTree);
  const contributedChildren = items.filter((item) => item.parentId);
  return items
    .filter((item) => !item.parentId)
    .map((item) => ({
      ...item,
      children: [...(item.children ?? []), ...contributedChildren.filter((child) => child.parentId === item.id)].sort((a, b) => a.order - b.order),
    }))
    .sort((a, b) => a.order - b.order);
}

export function composeAthleteTabs(modules: PlatformModule[], enabled: string[], permissions: ReadonlySet<string>): AthleteTab[] {
  return enabledOnly(modules, enabled)
    .flatMap((m) => m.athleteTabs ?? [])
    .filter((t) => hasAny(permissions, t.permission))
    .sort((a, b) => a.order - b.order);
}

/** The tabs of a sport's page: those the person may open, and (with the sport's `capabilities`) those the sport uses. */
export function composeSportTabs(modules: PlatformModule[], enabled: string[], permissions: ReadonlySet<string>, capabilities?: readonly string[]): SportTab[] {
  return enabledOnly(modules, enabled)
    .flatMap((m) => m.sportTabs ?? [])
    .filter((t) => hasAny(permissions, t.permission) && (!t.requires || !capabilities || capabilities.includes(t.requires)))
    .sort((a, b) => a.order - b.order);
}

export function composeQuickActions(modules: PlatformModule[], enabled: string[], permissions: ReadonlySet<string>): QuickAction[] {
  return enabledOnly(modules, enabled)
    .flatMap((m) => m.quickActions ?? [])
    .filter((a) => permissions.has(a.permission))
    .sort((a, b) => a.order - b.order);
}

export function composeSettingsSections(modules: PlatformModule[], enabled: string[], permissions: ReadonlySet<string>): SettingsSection[] {
  return enabledOnly(modules, enabled)
    .flatMap((m) => m.settingsSections ?? [])
    .filter((s) => apiMode === 'mock' || s.live)
    .filter((s) => hasAny(permissions, s.permission))
    .sort((a, b) => a.order - b.order);
}

export function composeReports(modules: PlatformModule[], enabled: string[], permissions: ReadonlySet<string>): ReportEntry[] {
  return enabledOnly(modules, enabled)
    .flatMap((m) => m.reports ?? [])
    .filter((r) => hasAny(permissions, r.permission))
    .filter((r) => r.showInReports !== false)
    .sort((a, b) => a.order - b.order);
}

/** The access groupings of the installed apps, in the order the apps stand: one dropdown each on the Users screen. */
export function composeAccess(modules: PlatformModule[], enabled: string[]): AppAccess[] {
  return enabledOnly(modules, enabled)
    .flatMap((m) => (m.access ? [m.access] : []))
    .sort((a, b) => a.order - b.order);
}

/** The home a person lands on, or null when no installed module offers one they may use. */
export function composeHome(modules: PlatformModule[], enabled: string[], permissions: ReadonlySet<string>): HomePage | null {
  const homes = enabledOnly(modules, enabled)
    .flatMap((m) => (m.home ? [m.home] : []))
    .filter((h) => hasAny(permissions, h.permission))
    .sort((a, b) => a.order - b.order);
  return homes[0] ?? null;
}

/** The kinds of result the installed apps can put in the global search, in the order the apps stand. */
export function composeSearchKinds(modules: PlatformModule[], enabled: string[]): SearchKind[] {
  return enabledOnly(modules, enabled).flatMap((m) => m.searchKinds ?? []);
}

export function composeSlot<N extends SlotName>(modules: PlatformModule[], enabled: string[], permissions: ReadonlySet<string>, name: N): SlotContribution<N>[] {
  return enabledOnly(modules, enabled)
    .flatMap((m) => (m.slots?.[name] ?? []) as SlotContribution<N>[])
    .filter((c) => hasAny(permissions, c.permission))
    .sort((a, b) => a.order - b.order);
}
